import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuthStore } from '../stores/auth'
import { subscribeRawFrames } from '../hooks/useConsoleLive'
import type { ChangeAction, LiveStatus, ServerMessage } from '../api/ws'
import { Chip } from './ui'
import type { ChipProps } from './ui'
import { timeOfDay } from '../lib/format'

/** Ring-buffer capacity — oldest lines drop beyond it. */
const MAX_LINES = 500
/** Two wraps within this window collapse into ONE updated marker line. */
const BURST_WINDOW_MS = 1_000
/** localStorage key for the hidden-kinds filter set. */
const HIDDEN_KEY = 'ostrel.ops-tail.hidden-kinds'

interface FrameLine {
  id: number
  at: string
  kind: string
  action: ChangeAction | null
  summary: string
  raw: string
}
/** A burst marker: the buffer wrapped twice within a second — N lines went
 *  over the ring's edge without being displayed. */
interface MarkerLine {
  id: number
  marker: true
  count: number
}
type TailLine = FrameLine | MarkerLine

let nextLineId = 1

/** One line per frame: 'updated door odr-3375671 — 13 - PB Lgh 71001'. */
function toLine(msg: ServerMessage): FrameLine {
  const at = 'at' in msg && msg.at ? timeOfDay(msg.at) : timeOfDay(new Date().toISOString())
  if (msg.type === 'change') {
    const obj = msg.object as unknown as Record<string, unknown>
    const name = (obj.display_name ?? obj.name ?? obj.code ?? obj.title ?? '') as string
    const uid = (obj.unique_id as string) ?? ''
    return {
      id: nextLineId++,
      at,
      kind: msg.kind,
      action: msg.action,
      summary: `${msg.action} ${msg.kind.replace(/_/g, ' ')} ${uid}${name ? ` — ${name}` : ''}`,
      raw: JSON.stringify(msg, null, 2),
    }
  }
  return {
    id: nextLineId++,
    at,
    kind: msg.type,
    action: null,
    summary: msg.type,
    raw: JSON.stringify(msg, null, 2),
  }
}

interface Buffer {
  lines: TailLine[]
  dropCount: number
  lastWrapAt: number
}

/** Append one line, capping at MAX_LINES. A wrap within a second of the
 *  previous one updates the trailing 'dropped N lines' marker instead of
 *  adding another. */
function appendLine(buf: Buffer, line: FrameLine): Buffer {
  let lines = [...buf.lines, line]
  let { dropCount, lastWrapAt } = buf
  if (lines.length > MAX_LINES) {
    const overflow = lines.length - MAX_LINES
    lines = lines.slice(overflow)
    const now = Date.now()
    const within = now - lastWrapAt < BURST_WINDOW_MS
    lastWrapAt = now
    dropCount = within ? dropCount + overflow : overflow
    const last = lines[lines.length - 1]
    if (within && last && 'marker' in last) {
      lines = [...lines.slice(0, -1), { ...last, count: dropCount }]
    } else {
      lines = [...lines, { id: nextLineId++, marker: true, count: dropCount }]
    }
  }
  return { lines, dropCount, lastWrapAt }
}

function loadHidden(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [])
  } catch {
    return new Set()
  }
}

const actionTone = (action: ChangeAction | null): ChipProps['tone'] =>
  action === 'created' ? 'ok' : action === 'deleted' ? 'danger' : action === 'updated' ? 'info' : 'muted'

const drawerBtn =
  'rounded-sm border border-ostrel-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ostrel-dim transition-colors hover:text-ostrel-light focus:outline-none focus:ring-1 focus:ring-ostrel-gold'

/**
 * Staff-only diagnostics drawer (spec 2026-08-11): a Quake-style bottom
 * drawer tailing the raw /ws channel, one line per frame. Gated on
 * `principal.is_superadmin` — for anyone else there is no glyph, the
 * backtick does nothing, and the frame tap never attaches (it subscribes
 * only while the drawer is OPEN, never while closed).
 */
export function OpsTailDrawer({ live }: { live: LiveStatus }) {
  const isStaff = useAuthStore((s) => s.principal?.is_superadmin === true)
  const [open, setOpen] = useState(false)
  const [lines, setLines] = useState<TailLine[]>([])
  const [paused, setPaused] = useState(false)
  const [copied, setCopied] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [hidden, setHidden] = useState<ReadonlySet<string>>(loadHidden)
  const bufRef = useRef<Buffer>({ lines: [], dropCount: 0, lastWrapAt: 0 })
  const pausedRef = useRef(paused)
  pausedRef.current = paused
  const scrollRef = useRef<HTMLDivElement>(null)

  // Backtick (or § on Nordic layouts) toggles — never while typing.
  useEffect(() => {
    if (!isStaff) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '`' && e.key !== '§') return
      const t = e.target as HTMLElement | null
      if (
        t instanceof HTMLInputElement ||
        t instanceof HTMLTextAreaElement ||
        t instanceof HTMLSelectElement ||
        t?.isContentEditable
      ) {
        return
      }
      e.preventDefault()
      setOpen((v) => !v)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isStaff])

  // The raw-frame tap attaches only while the drawer is open.
  useEffect(() => {
    if (!isStaff || !open) return
    return subscribeRawFrames((msg) => {
      if (pausedRef.current) return
      bufRef.current = appendLine(bufRef.current, toLine(msg))
      setLines(bufRef.current.lines)
    })
  }, [isStaff, open])

  // Follow the tail.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [lines])

  /** Filter chips: the kinds seen so far, sorted. */
  const kinds = useMemo(() => {
    const seen = new Set<string>()
    for (const l of lines) if (!('marker' in l)) seen.add(l.kind)
    return [...seen].sort()
  }, [lines])

  const visible = lines.filter((l) => 'marker' in l || !hidden.has(l.kind))

  if (!isStaff) return null

  const toggleKind = (kind: string) => {
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(kind)) next.delete(kind)
      else next.add(kind)
      localStorage.setItem(HIDDEN_KEY, JSON.stringify([...next]))
      return next
    })
  }

  const clear = () => {
    bufRef.current = { lines: [], dropCount: 0, lastWrapAt: 0 }
    setLines([])
    setExpanded(null)
  }

  const copyVisible = async () => {
    const text = visible
      .map((l) => ('marker' in l ? `··· dropped ${l.count} lines ···` : `${l.at} ${l.summary}`))
      .join('\n')
    await navigator.clipboard.writeText(text)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Ops tail (`)"
        aria-label="Ops tail"
        aria-expanded={open}
        className="rounded-sm px-1 font-mono text-[10px] text-ostrel-dim transition-colors hover:text-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
      >
        {'>_'}
      </button>
      {open && (
        <div
          role="region"
          aria-label="Ops tail"
          className="fixed inset-x-0 bottom-0 z-50 flex h-[35vh] flex-col border-t border-ostrel-border bg-ostrel-navy shadow-2xl"
        >
          <div className="flex flex-wrap items-center gap-2 border-b border-ostrel-border px-4 py-2">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-ostrel-dim">
              ops tail
            </span>
            <span className="flex items-center gap-1.5 font-mono text-[10px] text-ostrel-dim">
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                  live === 'connected' ? 'bg-ostrel-ok' : live === 'connecting' ? 'bg-ostrel-warn' : 'bg-ostrel-danger'
                }`}
              />
              {live}
            </span>
            <span className="tnum font-mono text-[10px] text-ostrel-dim">
              {visible.length}/{lines.length}
            </span>
            <span className="ml-auto flex items-center gap-1.5">
              <button type="button" className={drawerBtn} onClick={() => setPaused((v) => !v)}>
                {paused ? 'Resume' : 'Pause'}
              </button>
              <button type="button" className={drawerBtn} onClick={clear}>
                Clear
              </button>
              <button type="button" className={drawerBtn} onClick={() => void copyVisible()}>
                {copied ? 'Copied' : 'Copy visible'}
              </button>
              <button
                type="button"
                className={drawerBtn}
                aria-label="Close ops tail"
                onClick={() => setOpen(false)}
              >
                ✕
              </button>
            </span>
          </div>
          {kinds.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 border-b border-ostrel-border px-4 py-1.5">
              {kinds.map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={!hidden.has(k)}
                  onClick={() => toggleKind(k)}
                  className={`rounded-sm border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider transition-colors focus:outline-none focus:ring-1 focus:ring-ostrel-gold ${
                    hidden.has(k)
                      ? 'border-ostrel-border text-ostrel-dim line-through'
                      : 'border-ostrel-gold/50 bg-ostrel-gold/10 text-ostrel-gold'
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          )}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-2 font-mono text-[11px] leading-relaxed">
            {visible.length === 0 ? (
              <p className="text-ostrel-dim">No frames yet — frames appear as the channel streams.</p>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {visible.map((l) =>
                  'marker' in l ? (
                    <li key={l.id} className="text-ostrel-warn">
                      ··· dropped {l.count} lines ···
                    </li>
                  ) : (
                    <li key={l.id}>
                      <button
                        type="button"
                        onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                        className="flex w-full items-center gap-2 text-left text-ostrel-muted transition-colors hover:text-ostrel-light"
                      >
                        <span className="tnum shrink-0 text-ostrel-dim">{l.at}</span>
                        <Chip tone={actionTone(l.action)} label={l.kind} />
                        <span className="min-w-0 truncate">{l.summary}</span>
                      </button>
                      {expanded === l.id && (
                        <pre className="mt-1 max-h-48 overflow-auto rounded-sm border border-ostrel-border p-2 text-[10px] text-ostrel-muted">
                          {l.raw}
                        </pre>
                      )}
                    </li>
                  ),
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  )
}
